// src/services/categoryService.ts

export interface Category {
  id: string;
  name: string;
  icon?: string; // Add icon property
}

class CategoryService {
  private categories = {
    en: [
      { id: "kids_science", name: "Science Kits for Kids", icon: "🧪" },
      { id: "robotics_electronics", name: "Robotics, Electronics & Software", icon: "🤖" },
      { id: "microscopes_telescopes", name: "Microscopes, Telescopes & Optics", icon: "🔬" },
      { id: "globes_concepts", name: "Globes & Concepts", icon: "🌍" },
      { id: "math_logic", name: "Math & Logic", icon: "🧮" },
      { id: "creative_kits", name: "Creative Kits", icon: "🎨" },
      { id: "building_engineering", name: "Construction & Engineering", icon: "📊️" },
      { id: "outdoor_games", name: "Outdoor & Yard Games", icon: "🎯" },
      { id: "science_equipment", name: "Scientific Equipment", icon: "⚗️" }
    ],
    ar: [
      { id: "kids_science", name: "مجموعات تجارب علمية", icon: "🧪" },
      { id: "robotics_electronics", name: "روبوتيكا، إلكترونيكا وبرمجة", icon: "🤖" },
      { id: "microscopes_telescopes", name: "ميكروسكوبات، تلسكوبات ومجاهر", icon: "🔬" },
      { id: "globes_concepts", name: "كرات أرضية ومفاهيم", icon: "🌍" },
      { id: "math_logic", name: "الرياضيات والمنطق", icon: "🧮" },
      { id: "creative_kits", name: "أدوات إبداعية", icon: "🎨" },
      { id: "building_engineering", name: "الهندسة والبناء", icon: "📊️" },
      { id: "outdoor_games", name: "ألعاب خارجية وساحة", icon: "🎯" },
      { id: "science_equipment", name: "معدات علمية", icon: "⚗️" }
    ]
  };
  public getCategoryIcon(categoryId: string): string {
    const category = this.getCategoryById(categoryId, 'en');
    return category?.icon || "📚"; // Default icon
  }

  /**
   * Get all categories for a specific language
   * @param language 'en' or 'ar'
   * @returns Array of categories
   */
  public getAllCategories(language: 'en' | 'ar'): Category[] {
    return this.categories[language];
  }

  /**
   * Get a category by ID for a specific language
   * @param categoryId The category ID
   * @param language 'en' or 'ar'
   * @returns The category or undefined if not found
   */
  public getCategoryById(categoryId: string, language: 'en' | 'ar'): Category | undefined {
    return this.categories[language].find(cat => cat.id === categoryId);
  }

  /**
   * Get category name by ID for English
   * @param categoryId The category ID
   * @returns The category name or the ID if not found
   */
  public getCategoryNameEn(categoryId: string): string {
    const category = this.getCategoryById(categoryId, 'en');
    return category?.name || categoryId;
  }

  /**
   * Get category name by ID for Arabic
   * @param categoryId The category ID
   * @returns The category name or the ID if not found
   */
  public getCategoryNameAr(categoryId: string): string {
    const category = this.getCategoryById(categoryId, 'ar');
    return category?.name || categoryId;
  }

  /**
   * Get category name by ID for the current language
   * @param categoryId The category ID
   * @param language 'en' or 'ar'
   * @returns The category name or the ID if not found
   */
  public getCategoryName(categoryId: string, language: 'en' | 'ar'): string {
    return language === 'ar'
      ? this.getCategoryNameAr(categoryId)
      : this.getCategoryNameEn(categoryId);
  }
}

// Export a singleton instance
export const categoryService = new CategoryService();

// Export default for direct import
export default categoryService;